import {
  ComponentContent,
  ImageContentFragment,
  ProductTableProductVariantFragment,
  SelectionFragment,
  SingleLineFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import {
  CrystallizeProductTypeDefault,
  CrystallizeProductTypeValues,
} from '@/integrations/crystallize/types/constants';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';
import { AttributeTileDetail } from '@/ui/components/pages/ProductDetail/types/attribute-tile-detail';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { parsePriceVariant } from '@/ui/utils/parsePriceVariant';
import { parseWarehouseDetails } from '@/ui/components/pages/ProductDetail/utils/parseWarehouseDetails';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { CartItem } from '@/ui/store/cart/types/CartItem';
import { generateRandomId } from '@/ui/utils/helpers';
import { DocShapeId } from '@/utils/shapeIds';
import { parseRestrictedDisplayMode } from '@/utils/tile-country-visibility';

// Shapes that can be added straight to the cart from a Product Cards tile when "show chooser" is off.
const DIRECT_ADD_SHAPES = new Set([DocShapeId.accessory, DocShapeId.sparePart]);

/**
 * Accessory and spare-part variants are added to the cart directly from the tile when their
 * "show chooser" toggle is not enabled (off or unset). This mirrors the accessory PDP, whose chooser
 * shows only when the toggle is explicitly true (`showChooser || false`). Any other shape keeps its
 * regular PDP/chooser flow.
 */
export function isDirectAddToCartVariant(variant: ProductTableProductVariantFragment): boolean {
  const shapeIdentifier = variant.product?.shape.identifier;
  if (!shapeIdentifier || !DIRECT_ADD_SHAPES.has(shapeIdentifier as DocShapeId)) {
    return false;
  }

  return (
    componentByTypename(variant.product?.showChooser?.content, 'BooleanContent')?.value !== true
  );
}

/**
 * Variant-level "Variant description" rich text (a Spare Part shape variant component). Returns null
 * when the variant has no description, so callers can fall back to the product short description.
 */
export function getVariantDescriptionBlocks(variant: ProductTableProductVariantFragment) {
  return componentByTypename(variant.variantDescription?.content, 'RichTextContent');
}

/**
 * Builds a cart item from a tile variant without needing a parsed BaseProduct instance — the tile
 * fragment already carries everything CartService needs (delivery time is resolved with defaults).
 */
export function buildCartItemFromVariant(variant: ProductTableProductVariantFragment): CartItem {
  return {
    id: variant.product?.id ?? '',
    cartItemId: generateRandomId(),
    shape: variant.product?.shape.identifier ?? '',
    sku: variant.sku,
    url: variant.product?.path ?? '',
    image: variant.images?.[0],
    name: variant.name ?? '',
    quantity: 1,
    priceVariant: parsePriceVariant(variant.priceVariant),
  };
}

/**
 * Minimal parse of a tile variant into the shape expected by `isProductVariantInStock` and the
 * Notify-Me store, so the existing stock/OOS logic can be reused as-is.
 */
export function parseVariantForStock(
  variant: ProductTableProductVariantFragment,
): ParsedProductVariant {
  return {
    name: variant.name ?? '',
    sku: variant.sku,
    isDefault: variant.isDefault ?? false,
    priceVariant: parsePriceVariant(variant.priceVariant),
    images: variant.images ?? [],
    videos: [],
    productType:
      (componentByTypename(variant.productType?.content, 'SelectionContent')?.options?.[0]
        ?.key as CrystallizeProductTypeValues) || CrystallizeProductTypeDefault,
    warehouseDetails: parseWarehouseDetails(variant.warehouseDetails) ?? null,
    stockCount: variant.stock ?? null,
    isGlobal: componentByTypename(variant.isGlobal?.content, 'BooleanContent')?.value ?? null,
  };
}

/**
 * The colour selection holds one option: its key is the display name, its value the hex code.
 */
export function parseColorDetail(
  content: SelectionFragment | null | undefined,
): ColorDetail | undefined {
  const colorOption = content?.options?.[0];
  return colorOption ? { name: colorOption.key, hex: colorOption.value } : undefined;
}

type ComponentContentTypename = NonNullable<ComponentContent['__typename']>;

type TileLeafTypename = 'SingleLineContent' | 'SelectionContent' | 'ImageContent';

type OtherContentTypename = Exclude<ComponentContentTypename, TileLeafTypename>;

// Only the leaf contents the tile parser reads are typed; every other content stays opaque,
// so the full product fragments and the slim nested-variant fragment both fit.
type RelationTileComponentContent =
  | SingleLineFragment
  | (SelectionFragment & { __typename: 'SelectionContent' })
  | ImageContentFragment
  | { __typename: OtherContentTypename };

type RelationTileComponent = {
  id?: string;
  content?: RelationTileComponentContent | null;
} | null;

// Two query shapes feed the tile parser: the product page's own relations fetch every component
// as `components[]`, while the nested bundle-variant fragment (carried by every product list)
// fetches only the components the tile needs, aliased by id to keep that payload small.
export interface RelationTileItem {
  topics?: { path: string }[] | null;
  components?: { id: string; content?: RelationTileComponentContent | null }[] | null;
  tileType?: RelationTileComponent;
  tileName?: RelationTileComponent;
  tileImage?: RelationTileComponent;
  restrictionDisplayMode?: RelationTileComponent;
}

export interface RelationTileContent {
  items?: RelationTileItem[] | null;
}

export interface ItemWithTopicPaths {
  topics?: ReadonlyArray<{ path?: string | null } | null> | null;
}

// The country topics of an item for the country-visibility rules; undefined without any,
// so an untagged item stays visible everywhere.
export function extractTopicPaths(item: ItemWithTopicPaths): { path: string }[] | undefined {
  const paths = (item.topics ?? [])
    .filter((topic): topic is { path: string } => !!topic?.path)
    .map((topic) => ({ path: topic.path }));

  return paths.length > 0 ? paths : undefined;
}

type RelationTileComponentId = 'type' | 'name' | 'image' | 'country-restriction-display-mode';

const TILE_COMPONENT_ALIASES = {
  type: 'tileType',
  name: 'tileName',
  image: 'tileImage',
  'country-restriction-display-mode': 'restrictionDisplayMode',
} as const satisfies Record<RelationTileComponentId, keyof RelationTileItem>;

// The aliased field wins; `components[]` is the fallback for the full-component shape.
const findTileComponent = (item: RelationTileItem, id: RelationTileComponentId) =>
  item[TILE_COMPONENT_ALIASES[id]]?.content ??
  item.components?.find((component) => component.id === id)?.content;

/**
 * Parses an option document related to a variant (cylinder, attribute wildcard, ...) into a
 * tile. The related item exposes the cylinder shape's components: `type` (selection), `name`
 * (single line), `image` and the per-item country-restriction display mode, either aliased or
 * inside `components[]`.
 */
export function parseRelationTileDetail(
  content: RelationTileContent | null | undefined,
): AttributeTileDetail | undefined {
  // Only one item can be related, so the first one is the option.
  const relatedItem = content?.items?.[0];
  if (!relatedItem) {
    return undefined;
  }

  // Only one selection option can be chosen, and the tile shows the first image.
  const type = componentByTypename(findTileComponent(relatedItem, 'type'), 'SelectionContent')
    ?.options?.[0]?.value;
  const name = componentByTypename(
    findTileComponent(relatedItem, 'name'),
    'SingleLineContent',
  )?.text;
  const image = componentByTypename(findTileComponent(relatedItem, 'image'), 'ImageContent')
    ?.images?.[0];
  const displayModeValue = componentByTypename(
    findTileComponent(relatedItem, 'country-restriction-display-mode'),
    'SelectionContent',
  )?.options?.[0]?.value;

  if (!type || !name) {
    return undefined;
  }

  const topics = extractTopicPaths(relatedItem);
  const displayMode = displayModeValue ? parseRestrictedDisplayMode(displayModeValue) : undefined;

  return {
    type,
    name,
    image,
    topics,
    displayMode,
  };
}

/**
 * Picks the variant whose price represents a whole linked product. Editors link whole products, so
 * the relation-level `productVariants` list is empty and the price must come from the product's own
 * variants. The default variant is what the PDP preselects; the first one only fills a product
 * without a default.
 */
export function getDefaultOrFirstVariant<T extends { isDefault?: boolean | null }>(
  variants: readonly T[] | null | undefined,
): T | undefined {
  return variants?.find((variant) => variant.isDefault) ?? variants?.[0];
}
